import React, { useState, useRef, useEffect } from "react";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { format } from "date-fns";

interface DateRangePickerProps {
  onChange: (range: { startDate: Date; endDate: Date }) => void;
  ranges: { startDate: Date; endDate: Date; key: string };
  dateFilterApplied?: boolean;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  onChange,
  ranges,
  dateFilterApplied,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => setIsOpen(!isOpen);

  const handleChange = (ranges: any) => {
    const { selection } = ranges;
    onChange({ startDate: selection.startDate, endDate: selection.endDate });

    // Only close once start and end are genuinely different days
    if (selection.startDate.getTime() !== selection.endDate.getTime()) {
      setIsOpen(false);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (
      pickerRef.current &&
      !pickerRef.current.contains(event.target as Node)
    ) {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="relative w-full sm:w-auto" ref={pickerRef}>
  <div
    className="w-full sm:w-auto border border-gray-300 rounded-2xl px-3 sm:px-4 py-2 cursor-pointer flex items-center justify-between gap-2 bg-white"
    onClick={handleToggle}
  >
    <span
      className={`min-w-0 truncate ${
        dateFilterApplied === false ? "text-[#808080]" : "text-black"
      }`}
    >
      {`${format(ranges.startDate, "MMM dd, yyyy")} - ${format(
        ranges.endDate,
        "MMM dd, yyyy",
      )}`}
    </span>

    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={`h-5 w-5 shrink-0 transform transition-transform ${
        dateFilterApplied === false ? "text-[#808080]" : "text-black"
      } ${isOpen ? "rotate-180" : ""}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M19 9l-7 7-7-7"
      />
    </svg>
  </div>

  {isOpen && (
    <div className="absolute z-50 mt-2 left-0 sm:left-auto sm:right-0 bg-white shadow-lg border rounded-lg max-w-[calc(100vw-20px)] overflow-x-auto">
      <DateRange
        ranges={[ranges]}
        onChange={handleChange}
        moveRangeOnFirstSelection={false}
        // editableDateInputs
        rangeColors={["#3b82f6"]}
        maxDate={new Date()}
      />
    </div>
  )}
</div>
  );
};

export default DateRangePicker;
